import {
  Autocomplete,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { getLabels, getQuery } from "./helpers";
import { SUPPORT_REQUEST_CREATE } from "./constants";
import { useLazyQuery, useMutation } from "@apollo/client";
import { PhoneNumberInput, useLanguageTranslator } from "../../../components";
import { SuccessModal } from "./successModal";
import { Trans } from "react-i18next";

function ContactForm() {
  const [isOpen, setIsOpen] = useState(false);

  const { translator } = useLanguageTranslator();

  const form = useForm<any>({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      category_id: null,
      sub_category_id: null,
      message: "",
    },
  });

  const { watch, setValue, trigger } = form;
  const formData = watch();

  const subDropdownLabels = useMemo(
    () => getLabels(formData?.category_id),
    [formData?.category_id]
  );

  const [fetchData, { data, loading }] = useLazyQuery(
    getQuery(formData?.category_id),
    {
      fetchPolicy: "network-only",
    }
  );

  const [
    mutate,
    { data: supportData, loading: supportLoading, error: supportError },
  ] = useMutation(SUPPORT_REQUEST_CREATE, {
    onCompleted: (data) => {
      if (data?.supportRequestCreate?.supportRequest?.id) {
        setIsOpen(true);
      }
    },
    onError: (error) => {
      alert("Request Failed");
      console.error("Mutation failed:", error);
    },
  });

  useEffect(() => {
    if (formData?.category_id !== "") {
      fetchData();
    }
  }, [formData?.category_id, fetchData]);

  const sectionsData =
    data?.policies?.collection ||
    data?.practices?.collection ||
    data?.people?.collection;

  const modifiedData =
    sectionsData?.map(({ id: value, title: label, ...rest }: any) => ({
      value,
      label,
      ...rest,
    })) || [];

  const handleFinish = useCallback(
    (data: any) => {
      const payload = {
        firstname: data?.first_name,
        lastname: data?.last_name,
        email: data?.email,
        ...(data?.phone_number ? { phoneNumber: data?.phone_number } : {}),
        ...(formData?.category_id === "1"
          ? { policyId: data?.sub_category_id?.value }
          : {}),
        ...(formData?.category_id === "2"
          ? { practiceId: data?.sub_category_id?.value }
          : {}),
        ...(formData?.category_id === "3"
          ? { personId: data?.sub_category_id?.value }
          : {}),
        message: data?.message,
      };

      mutate({
        variables: {
          input: payload,
        },
      });
    },
    [formData?.category_id]
  );

  function onClose() {
    setIsOpen(false);
  }

  return (
    <>
      <SuccessModal
        isOpen={isOpen}
        onClose={onClose}
        message={"Successfully sent a message"}
        path={"/dashboard"}
      />
      <Typography textAlign={"center"} fontWeight={600} fontSize="31px" mb={3}>
        {translator("support.Get in touch")}
      </Typography>
      <Typography textAlign={"center"} fontSize="16px" mb={5}>
        <Trans i18nKey="support.supportText" t={translator}>
          Our dedicated support team is committed to providing you with the
          assistance you need. Whether you have questions, need troubleshooting,
          or want to share feedback, we're just a contact away. Get in touch.
        </Trans>
      </Typography>
      <Divider sx={{ margin: "0px 0px 40px 0px" }} />
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleFinish)}>
          <Grid container spacing={2}>
            <Grid item lg={6}>
              <Controller
                name="first_name"
                // rules={{ required: "Required!" }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    fullWidth
                    required
                    error={!!error}
                    disabled={false}
                  >
                    <FormLabel>{translator("support.First Name")}</FormLabel>
                    <OutlinedInput
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        form?.trigger(field.name);
                      }}
                    />
                    <FormHelperText>{error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                name="last_name"
                // rules={{ required: "Required!" }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    fullWidth
                    required
                    error={!!error}
                    disabled={false}
                    sx={{ marginTop: "15px" }}
                  >
                    <FormLabel>{translator("support.Last Name")}</FormLabel>
                    <OutlinedInput
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        form?.trigger(field.name);
                      }}
                    />
                    <FormHelperText>{error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                name="email"
                rules={{
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                    message: "Email is in wrong format",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    fullWidth
                    required
                    error={!!error}
                    disabled={false}
                    sx={{ marginTop: "15px" }}
                  >
                    <FormLabel>{translator("support.Email")}</FormLabel>
                    <OutlinedInput
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        form?.trigger(field.name);
                      }}
                    />
                    <FormHelperText>{error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                name="phone_number"
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    fullWidth
                    error={!!error}
                    disabled={false}
                    sx={{ marginTop: "15px" }}
                  >
                    <FormLabel>{translator("support.Phone Number")}</FormLabel>
                    {/* <OutlinedInput
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        form?.trigger(field.name);
                      }}
                    /> */}
                    <PhoneNumberInput
                      // value={field?.phone_number}
                      onChange={(value: any) => {
                        field.onChange(value);
                        form?.trigger(`phone_number`);
                      }}
                    />
                    <FormHelperText>{error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item lg={6}>
              <Controller
                name="category_id"
                render={({ field, fieldState: { error } }) => (
                  <FormControl error={!!error} disabled={false} fullWidth>
                    <FormLabel>
                      {translator("dashboardTableHead.Category")}
                    </FormLabel>
                    <Select
                      value={field.value || ""}
                      onChange={(e) => {
                        field.onChange(e);
                        form?.trigger(field.name);
                        setValue("sub_category_id", "");
                        trigger("category");
                      }}
                      displayEmpty
                      inputProps={{
                        "aria-label": "Select a country",
                      }}
                    >
                      <MenuItem value="1">{translator("Policies")}</MenuItem>
                      <MenuItem value="2">{translator("Practices")}</MenuItem>
                      <MenuItem value="3">{translator("People")}</MenuItem>
                    </Select>
                    <FormHelperText>{error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                name="sub_category_id"
                rules={{
                  required: formData?.category_id ? "Required!" : "",
                }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    error={!!error}
                    disabled={false}
                    fullWidth
                    sx={{ marginTop: "15px" }}
                  >
                    <FormLabel>
                      {translator("support.Select")}{" "}
                      {translator(subDropdownLabels || "")}
                    </FormLabel>
                    <Autocomplete
                      disablePortal
                      options={modifiedData || []}
                      sx={{ width: "100%" }}
                      value={field.value || ""}
                      onChange={(event: any, newValue: any) => {
                        field.onChange(newValue);
                        form?.trigger(field.name);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                    <FormHelperText>{error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                name="message"
                rules={{ required: "Message is required" }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    required
                    error={!!error}
                    disabled={false}
                    fullWidth
                    sx={{ marginTop: "15px" }}
                  >
                    <FormLabel>{translator("support.Message")}</FormLabel>
                    <TextField
                      multiline
                      rows={3}
                      placeholder={translator("support.Enter a message")}
                      onChange={(e) => {
                        field.onChange(e);
                        form?.trigger(field.name);
                      }}
                      value={field.value || ""}
                      error={!!error}
                    />
                    <FormHelperText>{error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
              <Button
                variant="contained"
                size="large"
                fullWidth
                sx={{ backgroundColor: "#F6A000", marginTop: "20px" }}
                type="submit"
              >
                {translator("support.Send message")}
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </>
  );
}

export { ContactForm };
